import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { SocialPostDialog } from "@/components/social/SocialPostGenerator";
import {
  Video, Upload, Trash2, Loader2, RefreshCw, Radio, Copy, Eye, EyeOff,
  Play, Globe, Lock, CheckCircle2, AlertTriangle, Clapperboard, Megaphone, Download, Mail,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function fmtDuration(s) {
  if (s == null || s < 0) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const VISIBILITY = {
  public: { label: "Public", icon: Globe, cls: "bg-[rgba(34,139,94,0.14)] text-[#1f7a52]" },
  members: { label: "Members", icon: Lock, cls: "bg-[rgba(201,119,46,0.16)] text-[#8A4A18]" },
};

function StatusPill({ v }) {
  if (v.ready_to_stream) return <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" />Ready</Badge>;
  if (v.status === "error") return <Badge variant="destructive">Error</Badge>;
  return <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" />Processing</Badge>;
}

function VisBadge({ v }) {
  const cfg = VISIBILITY[v.visibility] || VISIBILITY.members;
  const Icon = cfg.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.cls}`}><Icon className="h-3 w-3" />{cfg.label}</span>;
}

export default function AdminVideos() {
  const { can } = useAuth();
  const canEmail = can("manage_members");
  const [status, setStatus] = useState(null);
  const [videos, setVideos] = useState([]);
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("videos");
  const [postVideo, setPostVideo] = useState(null);

  // upload dialog
  const [upOpen, setUpOpen] = useState(false);
  const [upTitle, setUpTitle] = useState("");
  const [upDesc, setUpDesc] = useState("");
  const [upVis, setUpVis] = useState("members");
  const [upFile, setUpFile] = useState(null);
  const [upProgress, setUpProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // live dialog
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveName, setLiveName] = useState("");
  const [liveVis, setLiveVis] = useState("members");
  const [creatingLive, setCreatingLive] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [importingVods, setImportingVods] = useState(false);

  // player dialog
  const [player, setPlayer] = useState(null); // { title, iframe }
  const pollRef = useRef(null);

  const loadVideos = useCallback(async () => {
    const v = await api.get("/stream/videos").then((r) => r.data).catch(() => []);
    setVideos(v);
    return v;
  }, []);

  const load = useCallback(async () => {
    const [st, v, l] = await Promise.all([
      api.get("/stream/status").then((r) => r.data).catch(() => ({ configured: false })),
      api.get("/stream/videos").then((r) => r.data).catch(() => []),
      api.get("/stream/live").then((r) => r.data).catch(() => []),
    ]);
    setStatus(st); setVideos(v); setLives(l); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while any video is still processing.
  useEffect(() => {
    const pending = videos.some((v) => !v.ready_to_stream && v.status !== "error");
    if (!pending) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => { loadVideos(); }, 5000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [videos, loadVideos]);

  const resetUpload = () => { setUpTitle(""); setUpDesc(""); setUpVis("members"); setUpFile(null); setUpProgress(0); };

  const doUpload = async () => {
    if (!upTitle.trim()) { toast.error("Please add a title"); return; }
    if (!upFile) { toast.error("Please choose a video file"); return; }
    setUploading(true);
    setUpProgress(0);
    try {
      const init = await api.post("/stream/videos/direct-upload", {
        title: upTitle.trim(), description: upDesc.trim() || null, visibility: upVis,
      });
      const { uploadURL } = init.data;
      const fd = new FormData();
      fd.append("file", upFile);
      await axios.post(uploadURL, fd, {
        onUploadProgress: (e) => { if (e.total) setUpProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      toast.success("Uploaded — processing now");
      setUpOpen(false);
      resetUpload();
      await loadVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleVisibility = async (v) => {
    const next = v.visibility === "public" ? "members" : "public";
    try {
      await api.patch(`/stream/videos/${v.video_id}`, { visibility: next });
      toast.success(next === "public" ? "Now public" : "Now members-only");
      await loadVideos();
    } catch (e) { toast.error("Could not update visibility"); }
  };

  const emailMembers = async (v) => {
    try {
      const r = await api.post(`/admin/email/recordings/${v.video_id}/notify`);
      toast.success(`Replay notice going to ${r.data.recipients_count} members`);
      await loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the replay notice"); }
  };

  const deleteVideo = async (v) => {
    try { await api.delete(`/stream/videos/${v.video_id}`); toast.success("Deleted"); await loadVideos(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const openPlayer = async (v) => {
    if (v.iframe) { setPlayer({ title: v.title, iframe: v.iframe }); return; }
    try {
      const r = await api.get(`/stream/videos/${v.video_id}/play`);
      if (r.data.iframe) setPlayer({ title: v.title, iframe: r.data.iframe });
      else toast.error("Video isn't ready yet");
    } catch (e) { toast.error("Could not load player"); }
  };

  const createLive = async () => {
    if (!liveName.trim()) { toast.error("Name your broadcast"); return; }
    setCreatingLive(true);
    try {
      await api.post("/stream/live", { name: liveName.trim(), visibility: liveVis });
      toast.success("Live input created");
      setLiveOpen(false); setLiveName("");
      const l = await api.get("/stream/live").then((r) => r.data).catch(() => []);
      setLives(l);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create live input"); }
    finally { setCreatingLive(false); }
  };

  const deleteLive = async (l) => {
    try { await api.delete(`/stream/live/${l.live_id}`); toast.success("Deleted"); const r = await api.get("/stream/live").then((x) => x.data); setLives(r); }
    catch (e) { toast.error("Could not delete"); }
  };

  const importVods = async () => {
    setImportingVods(true);
    try {
      const r = await api.post("/recordings/import/cloudflare");
      const n = r.data?.imported || 0;
      if (n > 0) toast.success(`Imported ${n} recording${n === 1 ? "" : "s"} to your Recordings library`);
      else toast.info("No new recordings to import yet — they appear automatically after a broadcast ends.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import recordings");
    } finally { setImportingVods(false); }
  };

  const copy = (text, label) => navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});

  if (loading) return <SectionLoader />;
  const connected = status?.configured;

  return (
    <div className="space-y-6" data-testid="admin-videos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Videos</h1>
          <p className="mt-1 text-muted-foreground">Upload episodes & clips or go live with your show — powered by Cloudflare Stream.</p>
        </div>
        <Button variant="secondary" onClick={load} data-testid="videos-refresh"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
      </div>

      {/* Status */}
      <Card className="border-border bg-card" data-testid="videos-status-card">
        <CardContent className="flex flex-wrap items-center gap-4 p-5">
          <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${connected ? "bg-[rgba(34,139,94,0.14)] text-[#1f7a52]" : "bg-[rgba(201,119,46,0.16)] text-[#8A4A18]"}`}>
            {connected ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-foreground">{connected ? "Cloudflare Stream connected" : "Not configured"}</p>
              {connected && <Badge variant="secondary">Stream</Badge>}
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">{connected ? `${status.videos} video${status.videos === 1 ? "" : "s"} · ${status.live_inputs} live input${status.live_inputs === 1 ? "" : "s"}` : "Add Cloudflare Stream credentials to the backend env."}</p>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="videos" data-testid="tab-videos"><Clapperboard className="mr-2 h-4 w-4" />Library</TabsTrigger>
          <TabsTrigger value="live" data-testid="tab-live"><Radio className="mr-2 h-4 w-4" />Live</TabsTrigger>
        </TabsList>

        {/* ---------------- VOD ---------------- */}
        <TabsContent value="videos" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={upOpen} onOpenChange={(o) => { setUpOpen(o); if (!o) resetUpload(); }}>
              <DialogTrigger asChild>
                <Button disabled={!connected} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="video-upload-open"><Upload className="mr-2 h-4 w-4" />Upload video</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Upload a video</DialogTitle><DialogDescription>It uploads straight to Cloudflare, then processes for streaming.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label htmlFor="up-title">Title</Label><Input id="up-title" value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="Friday Conversations — Episode 12" data-testid="video-title-input" /></div>
                  <div><Label htmlFor="up-desc">Description (optional)</Label><Textarea id="up-desc" value={upDesc} onChange={(e) => setUpDesc(e.target.value)} rows={2} /></div>
                  <div>
                    <Label>Visibility</Label>
                    <Select value={upVis} onValueChange={setUpVis}>
                      <SelectTrigger data-testid="video-visibility-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="members">Members only (gated)</SelectItem>
                        <SelectItem value="public">Public (landing page)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="up-file">Video file</Label>
                    <Input id="up-file" type="file" accept="video/*" onChange={(e) => setUpFile(e.target.files?.[0] || null)} data-testid="video-file-input" />
                  </div>
                  {uploading && <Progress value={upProgress} className="h-2" data-testid="video-upload-progress" />}
                </div>
                <DialogFooter>
                  <Button onClick={doUpload} disabled={uploading} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="video-upload-submit">
                    {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading {upProgress}%</> : <><Upload className="mr-2 h-4 w-4" />Upload</>}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {videos.length === 0 ? (
            <EmptyState icon={Video} title="No videos yet" description="Upload your first episode or clip to get started." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {videos.map((v) => (
                <Card key={v.video_id} className="overflow-hidden border-border bg-card" data-testid="video-card">
                  <button type="button" onClick={() => v.ready_to_stream && openPlayer(v)} className="group relative flex h-40 w-full items-center justify-center border-b border-border bg-secondary/40">
                    {v.thumbnail ? (
                      <img src={v.thumbnail} alt={v.title} className="h-full w-full object-cover" />
                    ) : (
                      <Video className="h-10 w-10 text-muted-foreground" />
                    )}
                    {v.ready_to_stream && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
                        <Play className="h-10 w-10 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                      </span>
                    )}
                  </button>
                  <CardContent className="p-4">
                    <p className="truncate text-sm font-semibold text-foreground">{v.title}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <VisBadge v={v} />
                      <StatusPill v={v} />
                      <span className="text-xs text-muted-foreground">{fmtDuration(v.duration)} · {formatDate(v.created_at)}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      <Button size="sm" variant="ghost" disabled={!v.ready_to_stream} onClick={() => openPlayer(v)} data-testid="video-play"><Play className="mr-1.5 h-3.5 w-3.5" />Play</Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleVisibility(v)} data-testid="video-toggle-visibility">
                        {v.visibility === "public" ? <><Lock className="mr-1.5 h-3.5 w-3.5" />Make members</> : <><Globe className="mr-1.5 h-3.5 w-3.5" />Make public</>}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setPostVideo({ type: "video", id: v.video_id, title: v.title, public_url: null })} data-testid="video-createpost"><Megaphone className="mr-1.5 h-3.5 w-3.5" />Post</Button>
                      {canEmail && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" disabled={!v.ready_to_stream || v.visibility !== "public"} title={v.visibility !== "public" ? "Make the video public first" : v.members_notified_at ? `Members were emailed ${formatDate(v.members_notified_at)}` : "Email every member a link to this replay"} data-testid="video-email-members"><Mail className="mr-1.5 h-3.5 w-3.5" />{v.members_notified_at ? "Emailed" : "Email members"}</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="video-email-confirm">
                            <AlertDialogHeader><AlertDialogTitle>Email members about “{v.title}”?</AlertDialogTitle><AlertDialogDescription>Every active member who hasn’t opted out gets a “Watch the replay” email right now{v.members_notified_at ? " — they already received one for this video" : ""}.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => emailMembers(v)} className="bg-primary text-primary-foreground" data-testid="video-email-confirm-send">Send</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="ml-auto text-destructive" data-testid="video-delete"><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete this video?</AlertDialogTitle><AlertDialogDescription>This permanently removes it from Cloudflare Stream.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteVideo(v)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- LIVE ---------------- */}
        <TabsContent value="live" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" onClick={importVods} disabled={importingVods || !connected} data-testid="vod-import-button">
              {importingVods ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Import recordings
            </Button>
            <Dialog open={liveOpen} onOpenChange={setLiveOpen}>
              <DialogTrigger asChild>
                <Button disabled={!connected} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="live-create-open"><Radio className="mr-2 h-4 w-4" />Create live input</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create a live input</DialogTitle><DialogDescription>You'll get an RTMPS URL + stream key to paste into OBS or your encoder.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label htmlFor="live-name">Broadcast name</Label><Input id="live-name" value={liveName} onChange={(e) => setLiveName(e.target.value)} placeholder="Friday Conversations — Live" data-testid="live-name-input" /></div>
                  <div>
                    <Label>Visibility</Label>
                    <Select value={liveVis} onValueChange={setLiveVis}>
                      <SelectTrigger data-testid="live-visibility-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="members">Members only</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={createLive} disabled={creatingLive} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="live-create-submit">{creatingLive ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {lives.length === 0 ? (
            <EmptyState icon={Radio} title="No live inputs" description="Create one to stream your live show via OBS." />
          ) : (
            <div className="space-y-4">
              {lives.map((l) => (
                <Card key={l.live_id} className="border-border bg-card" data-testid="live-card">
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-foreground">{l.name}</p>
                      <VisBadge v={l} />
                      {l.status === "live" ? <Badge className="gap-1 bg-destructive text-destructive-foreground"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />LIVE</Badge> : <Badge variant="secondary">Idle</Badge>}
                      <div className="ml-auto flex gap-1">
                        {l.iframe && <Button size="sm" variant="ghost" onClick={() => setPlayer({ title: l.name, iframe: l.iframe })} data-testid="live-watch"><Eye className="mr-1.5 h-3.5 w-3.5" />Watch</Button>}
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid="live-delete"><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete this live input?</AlertDialogTitle><AlertDialogDescription>The RTMPS endpoint will stop working.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteLive(l)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3 rounded-lg border border-border bg-secondary/30 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">OBS / encoder settings</p>
                      <div>
                        <Label className="text-xs text-muted-foreground">RTMPS URL (Server)</Label>
                        <div className="mt-1 flex gap-2">
                          <Input readOnly value={l.rtmps_url || ""} className="font-mono text-xs" data-testid="live-rtmps-url" />
                          <Button size="icon" variant="secondary" onClick={() => copy(l.rtmps_url, "RTMPS URL")}><Copy className="h-4 w-4" /></Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Stream Key</Label>
                        <div className="mt-1 flex gap-2">
                          <Input readOnly type={revealed[l.live_id] ? "text" : "password"} value={l.rtmps_key || ""} className="font-mono text-xs" data-testid="live-stream-key" />
                          <Button size="icon" variant="secondary" onClick={() => setRevealed((r) => ({ ...r, [l.live_id]: !r[l.live_id] }))} data-testid="live-key-reveal">{revealed[l.live_id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                          <Button size="icon" variant="secondary" onClick={() => copy(l.rtmps_key, "Stream key")}><Copy className="h-4 w-4" /></Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">In OBS: Settings → Stream → Service "Custom", paste the Server + Stream Key, then "Start Streaming". Recordings are saved automatically and appear in your Library.</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Player dialog */}
      <Dialog open={!!player} onOpenChange={(o) => { if (!o) setPlayer(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{player?.title}</DialogTitle></DialogHeader>
          {player?.iframe && (
            <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
              <iframe src={player.iframe} title={player.title} className="h-full w-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen data-testid="video-player-iframe" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SocialPostDialog open={!!postVideo} onOpenChange={(o) => { if (!o) setPostVideo(null); }} source={postVideo} />
    </div>
  );
}
